import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import weighingService from '@/services/weighing';
import { Pesée, Produit } from '@/types';
import { useWeighing, useCurrentWeighing } from '@/contexts/WeighingContext';
import VehicleInfoSection from '@/components/VehicleInfoSection';
import OriginSection from '@/components/OriginSection';

import ClientSupplierSection from '@/components/ClientSupplierSection';

import HeaderTabs from '@/components/HeaderTabs';
import { WeighingProcess } from '@/components/modals/WeighingProcessModal';

import SearchableSelect from '@/components/SearchableSelect';
import WeighingStepper, { WeighingStep } from '@/components/WeighingStepper';
import WeightCapture from '@/components/WeightCapture';
import WeighingCodeDisplay from '@/components/WeighingCodeDisplay';
import { WeighingTicketData } from '@/components/WeighingTicket';
import TicketActions from '@/components/tickets/TicketActions';
import QualityPage from '@/pages/QualityPage';
import useWeighingWorkflow from '@/hooks/useWeighingWorkflow';
import { useAuth } from '@/hooks/useAuth'; // Hook pour récupérer l'utilisateur connecté
import { useScaleAgent } from '@/hooks/useScaleAgent';

import DestinationSection from '@/components/DestinationSection';
import TransferStickerSection from '@/components/TransferStickerSection';
import QualityAnalysisSection from '@/components/QualityAnalysisSection';
import CurrentWeighingInfo from '@/components/CurrentWeighingInfo';
import PendingWeighingsList from '@/components/PendingWeighingsList';

// Imports pour les permissions
import PermissionGuard from '@/components/permissions/PermissionGuard';
import PermissionButton from '@/components/permissions/PermissionButton';
import { PERMISSIONS } from '@/types/permissions';

const NouvellePeseePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { setCurrentWeighing, updateWeighing } = useWeighing();
  const currentWeighing = useCurrentWeighing();
  
  const [products, setProducts] = useState<Produit[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [conditionnements, setConditionnements] = useState<any[]>([]);
  const [conditionnementsLoading, setConditionnementsLoading] = useState(false);
  const [currentWeight, setCurrentWeight] = useState<number | null>(null);
  const [, setPontBasculeStatus] = useState<'connected' | 'disconnected' | 'error' | 'checking'>('checking');
  const [selectedPontBasculeId, setSelectedPontBasculeId] = useState<string>(''); // Pont bascule par défaut
  const [loadedWeighingId, setLoadedWeighingId] = useState<string | null>(null); // ID de la pesée actuellement chargée
  const [showWeighingDetailsModal, setShowWeighingDetailsModal] = useState(false);
  const [showTareSummaryModal, setShowTareSummaryModal] = useState(false);
  const [tareSummaryData, setTareSummaryData] = useState<any>(null);
  
  // États pour le formulaire de destination
  const [destinationFormData, setDestinationFormData] = useState({
    destination: '',
    magasin: '',
    numeroLot: ''
  });
  
  // weighingDetails est maintenant remplacé par currentWeighing du contexte
  // const [weighingDetails, setWeighingDetails] = useState<Pesée | null>(null);
  
  // États pour l'analyse qualité
  const [qualityAnalysis, setQualityAnalysis] = useState<any>(null);

  // États pour les informations du site et tenant
  const [siteInfo, setSiteInfo] = useState<{nom: string, id: string} | null>(null);
  const [tenantInfo, setTenantInfo] = useState<{nom: string, id: string} | null>(null);
  const [pontBasculeInfo, setPontBasculeInfo] = useState<{nom: string, site: string, tenant: string} | null>(null);

  // État pour indiquer si on charge une pesée existante
  const [isLoadingExistingWeighing, setIsLoadingExistingWeighing] = useState(false);
  const [showWeighingLoadedNotification, setShowWeighingLoadedNotification] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string>('');
  
  // États pour le modal des pesées en attente
  const [showPendingWeighingsModal, setShowPendingWeighingsModal] = useState(false);
  const [pendingWeighingsCount, setPendingWeighingsCount] = useState(0);

  // Fonction pour charger une pesée existante et déterminer l'étape
  const loadExistingWeighing = async (weighingId: string, forcedStep?: number) => {
    try {
      setIsLoadingExistingWeighing(true);
      setLoadingStep('Récupération des données de la pesée...');
      // Récupérer les détails de la pesée
      const response = await weighingService.getPesée(parseInt(weighingId));
      
      if (response.success && response.data) {
        const weighing = response.data as Pesée;
        // Déterminer l'étape appropriée
        let targetStep = 1;
        
        // Si un step est forcé via l'URL, l'utiliser
        if (forcedStep) {
          targetStep = forcedStep;
        } else {
          // Sinon, déterminer selon le statut
          if (weighing.statut === 'COMPLETEE') {
            // Pesée complétée → Étape 4 (Ticket final)
            targetStep = 4;
          } else if ((weighing.statut as string) === 'TARE_ENREGISTREE' || weighing.poids_tare) {
            // Tare enregistrée → Étape 4 (Ticket final)
            targetStep = 4;
          } else if (weighing.statut === 'EN_ATTENTE_TARE' || weighing.statut === 'BRUT_ENREGISTRE') {
            // En attente de tare ou brut enregistré → Étape 3 (Pesée tare)
            targetStep = 3;
          } else if (weighing.poids_brut && !weighing.poids_tare) {
            // Poids brut enregistré mais pas de tare → Étape 3 (Pesée tare)
            targetStep = 3;
          } else if (weighing.poids_brut) {
            // Poids brut enregistré → Étape 2 (Analyse qualité)
            targetStep = 2;
          } else {
            // Aucun poids enregistré → Étape 2 (Analyse qualité)
            targetStep = 2;
          }
        }
        setLoadingStep('Chargement des données du formulaire...');

        // Utiliser la fonction de synchronisation complète
        syncWeighingDataToForm(weighing);

        // Définir la pesée comme actuelle
        setCurrentWeighing(weighing);
        setLoadedWeighingId(weighingId);
        
        // Initialiser le processus de pesée depuis le backend
        if (weighing.processus) {
          initializeWeighingProcess(weighing.processus);
        } else {
          // Fallback vers le processus standard si non défini
          initializeWeighingProcess('standard');
        }

        // Aller à l'étape appropriée
        setLoadingStep('Redirection vers l\'étape appropriée...');
        goToStep(targetStep);

        // Récupérer l'analyse qualité si nécessaire
        if (targetStep >= 2) {
          setLoadingStep('Récupération de l\'analyse qualité...');
          await fetchQualityAnalysis(weighingId);
        }

        // Afficher la notification de succès
        setShowWeighingLoadedNotification(true);
        setTimeout(() => setShowWeighingLoadedNotification(false), 5000);
      } else {
        alert('Erreur lors du chargement de la pesée. Veuillez réessayer.');
      }
    } catch (error) {
      alert('Erreur lors du chargement de la pesée. Veuillez réessayer.');
    } finally {
      setIsLoadingExistingWeighing(false);
      setLoadingStep('');
    }
  };

  const { user } = useAuth();
  
  // Hook pour le scale agent
  const { currentWeight: scaleWeight, isConnected } = useScaleAgent(user?.sites?.[0]?.id, selectedPontBasculeId);
  
  

  
  // Fonction pour récupérer le premier pont bascule disponible
  const fetchDefaultPontBascule = async () => {
    try {
      const { PontBasculeService } = await import('@/services/pontBasculeService');
      const pontBasculeService = PontBasculeService.getInstance();
      
      const pontsBascule = await pontBasculeService.getAllPontsBascule();
      
      if (pontsBascule && pontsBascule.length > 0) {
        const firstPont = pontsBascule[0];
        setSelectedPontBasculeId(firstPont.id);
        return firstPont.id;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Fonction pour récupérer les informations du pont bascule, site et tenant
  const fetchPontBasculeInfo = async (pontBasculeId: string) => {
    if (!pontBasculeId) {
      return;
    }
    
    try {
      // Importer le service des ponts bascule
      const { PontBasculeService } = await import('@/services/pontBasculeService');
      const pontBasculeService = PontBasculeService.getInstance();
      
      const pontBascule = await pontBasculeService.getPontBasculeById(pontBasculeId);
      
      if (pontBascule) {
        setPontBasculeInfo({
          nom: pontBascule.name || `Pont ${pontBasculeId}`,
          site: pontBascule.site_name || 'Site principal',
          tenant: pontBascule.tenant_name || 'Tenant principal'
        });
        
        // Mettre à jour les informations du site et tenant
        if (pontBascule.site_name) {
          setSiteInfo({
            nom: pontBascule.site_name,
            id: pontBascule.siteId || ''
          });
        }
        
        if (pontBascule.tenant_name) {
          setTenantInfo({
            nom: pontBascule.tenant_name,
            id: pontBascule.tenantId || ''
          });
        }
      } else {
        // Valeurs par défaut si la récupération échoue
        setPontBasculeInfo({
          nom: `Pont ${pontBasculeId}`,
          site: 'Site principal',
          tenant: 'Tenant principal'
        });
        setSiteInfo({ nom: 'Site principal', id: pontBasculeId });
        setTenantInfo({ nom: 'Tenant principal', id: '1' });
      }
    } catch (err) {
      console.error('💥 fetchPontBasculeInfo: Exception capturée:', {
        error: err,
        message: err instanceof Error ? err.message : 'Erreur inconnue'
      });
      // Valeurs par défaut en cas d'erreur
      setPontBasculeInfo({
        nom: `Pont ${pontBasculeId}`,
        site: 'Site principal',
        tenant: 'Tenant principal'
      });
      setSiteInfo({ nom: 'Site principal', id: pontBasculeId });
      setTenantInfo({ nom: 'Tenant principal', id: '1' });
    }
  };
  
  // Fonction pour récupérer l'analyse qualité
  const fetchQualityAnalysis = async (weighingId: string) => {
    if (!weighingId) {
      return;
    }
    
  
    
    try {
      // Importer le service d'analyse qualité
      const qualityAnalysisService = (await import('@/services/qualityAnalysis')).default;
      
      
      const response = await (qualityAnalysisService as any).getQualityAnalysisByPeseeId(parseInt(weighingId));
      
     
      if (response.success && response.data) {
        setQualityAnalysis(response.data);
      } else {
        setQualityAnalysis(null);
      }
    } catch (err) {
      console.error('💥 fetchQualityAnalysis: Exception capturée:', {
        error: err,
        message: err instanceof Error ? err.message : 'Erreur inconnue'
      });
      setQualityAnalysis(null);
    } finally {
      console.log('🏁 fetchQualityAnalysis: Fin de la récupération');
    }
  };
  
  // Fonction pour récupérer les détails complets de la pesée
  const fetchWeighingDetails = async (weighingId: string) => {
    if (!weighingId) {
      return;
    }
    
    try {
      
      const response = await weighingService.getPesée(parseInt(weighingId));
      
      
      // Gérer différents formats de réponse
      let weighingData = null;
      
      if (response.success && response.data) {
        weighingData = response.data;
      } else if (response.data && !response.success) {
        weighingData = response.data;
      } else if ((response as any).id || (response as any).reference) {
        weighingData = response as unknown as Pesée;
      } else {
        console.error('Format de réponse inattendu:', {
          success: response.success,
          hasData: !!response.data,
          message: response.message || 'Aucun message'
        });
        return;
      }
      
      if (weighingData) {
        // weighingDetails est maintenant géré par le contexte
        // setWeighingDetails(weighingData as unknown as Pesée);
        
      }
    } catch (err) {
      console.error('💥 fetchWeighingDetails: Exception capturée:', {
        error: err,
        message: err instanceof Error ? err.message : 'Erreur inconnue'
      });
    } finally {
      console.log('🏁 fetchWeighingDetails: Fin de la récupération');
    }
  };

  // Hook pour gérer le workflow de pesée
  const {
    workflowState,
    formData,
    error: workflowError,
    saveGrossWeight,
    saveTareWeight,
    goToStep,
    nextStep,
    previousStep,
    updateFormData,
    canProceedToNextStep,
    
    // Nouvelles fonctions pour les processus
    getStepLabel,
    getCurrentWeighingType,
    initializeWeighingProcess,
    weighingProcess
  } = useWeighingWorkflow(selectedPontBasculeId);

  // Fonction pour synchroniser complètement les données de la pesée avec le formulaire
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const syncWeighingDataToForm = useCallback((weighing: Pesée) => {
    // Créer un objet de données complet pour la mise à jour en une seule fois
    const newFormData = {
      // Champs principaux
      typePesee: weighing.type_pesee || 'reception',
      produit: weighing.produit ? weighing.produit.toString() : '',
      conditionnement: weighing.conditionnement ? weighing.conditionnement.toString() : '',
      
      // Véhicule et transport
      vehicule: weighing.camion ? weighing.camion.toString() : '',
      remorque: weighing.remorque ? weighing.remorque.toString() : '',
      transporteur: weighing.transporteur ? weighing.transporteur.toString() : '',
      chauffeur: weighing.chauffeur ? weighing.chauffeur.toString() : '',
      convoyeur: weighing.convoyeur ? weighing.convoyeur.toString() : '',
      
      // Origine et destination
      provenance: weighing.provenance || '',
      connaissement: weighing.connaissement || '',
      
      // Partenaires commerciaux
      client: weighing.client ? weighing.client.toString() : '',
      fournisseur: weighing.fournisseur ? weighing.fournisseur.toString() : '',
      exportateur: weighing.exportateur ? weighing.exportateur.toString() : '',
      
      // Documents et références
      numeroTransfert: weighing.numero_transfert || '',
      sticker: weighing.sticker || '',
      
      // Observations
      observations: weighing.observations || '',
      
      // Champs d'analyse qualité - toujours avec des valeurs par défaut
      nbSacsDeclares: weighing.nb_sacs_declares || 0,
      poidsDeclares: weighing.poids_declares || 0,
      nbSacsSains: weighing.nb_sacs_sains || 0,
      nbSacsRecond: weighing.nb_sacs_recond || 0,
      nbSacsHumides: weighing.nb_sacs_humides || 0,
      nbSacsDechires: weighing.nb_sacs_dechires || 0,
      nbSacsRejetes: weighing.nb_sacs_rejetes || 0,
      nbSacsAcceptes: weighing.nb_sacs_acceptes || 0,
      poidsAcceptes: weighing.poids_acceptes || 0,
      
      // Champs supplémentaires (valeurs par défaut)
      refractionHum: 0,
      refractionQlt: 0
    };
    
    // Mettre à jour tous les champs en une seule fois
    Object.entries(newFormData).forEach(([field, value]) => {
    
      updateFormData(field as keyof typeof formData, value);
    });
    
    // Synchroniser les données de destination
    const destinationData = {
      destination: weighing.destination || '',
      magasin: weighing.magasin ? weighing.magasin.toString() : '',
      numeroLot: weighing.numero_lot || ''
    };
    
    setDestinationFormData(destinationData);
    
    // Synchroniser aussi les champs de destination dans formData
    updateFormData('destination', weighing.destination || '');
    updateFormData('magasin', weighing.magasin ? weighing.magasin.toString() : '');
    updateFormData('numeroLot', weighing.numero_lot || '');
    
    // Synchroniser le poids actuel
    if (weighing.poids_brut) {
      const weight = parseFloat(weighing.poids_brut.toString());
      setCurrentWeight(weight);
    }
  }, [updateFormData]);

  // Effet pour synchroniser les données entre les étapes
  useEffect(() => {
    // Synchroniser complètement les données quand on navigue entre les étapes
    if (currentWeighing && loadedWeighingId) {
      syncWeighingDataToForm(currentWeighing);
    }
  }, [currentWeighing, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour synchroniser les données à chaque changement d'étape
  useEffect(() => {
    if (currentWeighing && loadedWeighingId) {
      
      syncWeighingDataToForm(currentWeighing);
    }
  }, [workflowState.currentStep, currentWeighing, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour forcer la synchronisation après le chargement des produits et conditionnements
  useEffect(() => {
    if (currentWeighing && loadedWeighingId && products.length > 0 && conditionnements.length > 0) {
     
      // Plusieurs tentatives de synchronisation avec des délais différents
      setTimeout(() => {
        syncWeighingDataToForm(currentWeighing);
      }, 100);
      
      setTimeout(() => {
        
        syncWeighingDataToForm(currentWeighing);
      }, 500);
      
      setTimeout(() => {
        
        syncWeighingDataToForm(currentWeighing);
      }, 1000);
    }
  }, [products.length, conditionnements.length, currentWeighing, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour forcer la synchronisation quand on arrive à l'étape 1
  useEffect(() => {
    if (workflowState.currentStep === 1 && currentWeighing && loadedWeighingId) {
     
      setTimeout(() => {
        syncWeighingDataToForm(currentWeighing);
      }, 200);
    }
  }, [workflowState.currentStep, currentWeighing, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour vérifier et corriger l'état du formulaire après synchronisation
  useEffect(() => {
    if (currentWeighing && loadedWeighingId) {
      // Vérifier si les champs critiques sont vides après synchronisation
      const criticalFieldsEmpty = !formData.produit || !formData.vehicule || !formData.chauffeur;
      
      if (criticalFieldsEmpty) {
        
        // Re-synchroniser après un délai
        setTimeout(() => {
          
          syncWeighingDataToForm(currentWeighing);
        }, 200);
      } else {
        console.log('✅ Champs critiques correctement synchronisés', {
          produit: formData.produit,
          vehicule: formData.vehicule,
          chauffeur: formData.chauffeur
        });
      }
    }
  }, [formData.produit, formData.vehicule, formData.chauffeur, currentWeighing, loadedWeighingId, syncWeighingDataToForm]);

  // Effet pour mettre à jour automatiquement le poids net à chaque modification de currentWeight
  useEffect(() => {

    if (scaleWeight && scaleWeight.gross_weight) {
      // Le poids de la balance a changé
      // Le composant se re-rendra automatiquement
    }
  }, [scaleWeight?.gross_weight, currentWeighing?.poids_brut, workflowState.grossWeight]);

  // Initialiser le processus de pesée lors du changement de type de pesée
  useEffect(() => {
    if (formData.typePesee) {
      initializeWeighingProcess('standard');
    }
  }, [formData.typePesee, initializeWeighingProcess]);

  // Mettre automatiquement à jour le poids tare en étape 3 quand le scale agent envoie des données
  useEffect(() => {
    if (workflowState.currentStep === 3 && isConnected && scaleWeight && scaleWeight.weight !== undefined) {
      const weight = scaleWeight.weight;
      
      
      // Mettre à jour le poids tare dans le workflow
      saveTareWeight(weight); 
      // Mettre à jour le poids actuel affiché
      setCurrentWeight(weight);
      
      // Le poids net est calculé automatiquement via useMemo
    }
  }, [workflowState.currentStep, isConnected, scaleWeight, currentWeighing?.poids_brut, workflowState.grossWeight]);

  // Charger les produits et les informations du pont bascule au montage du composant
  useEffect(() => {
    const initializePage = async () => {
      // D'abord charger le pont bascule par défaut
      if (!selectedPontBasculeId) {
        const defaultPontId = await fetchDefaultPontBascule();
        if (defaultPontId) {
          // Le fetchDefaultPontBascule met déjà à jour selectedPontBasculeId
          // Donc on peut maintenant charger les autres données
          loadProducts();
          loadConditionnements();
          loadPendingWeighingsCount();
          fetchPontBasculeInfo(defaultPontId);
        } else {
          // Charger les autres données même sans pont bascule
          loadProducts();
          loadConditionnements();
          loadPendingWeighingsCount();
        }
      } else {
        // Pont bascule déjà sélectionné, charger les données
        loadProducts();
        loadConditionnements();
        loadPendingWeighingsCount();
        fetchPontBasculeInfo(selectedPontBasculeId);
      }
    };

    initializePage();
  }, [selectedPontBasculeId]);

  // Rafraîchir le nombre de pesées en attente périodiquement
  useEffect(() => {
    const interval = setInterval(() => {
      loadPendingWeighingsCount();
    }, 30000); // Rafraîchir toutes les 30 secondes

    return () => clearInterval(interval);
  }, []);

  // Rafraîchir le nombre de pesées en attente quand une pesée est créée ou modifiée
  useEffect(() => {
    if (currentWeighing?.id) {
      loadPendingWeighingsCount();
    }
  }, [currentWeighing?.id, currentWeighing?.statut]);

  // Vérifier si on doit charger une pesée existante depuis l'URL
  useEffect(() => {
    const weighingId = searchParams.get('weighingId');
    const stepParam = searchParams.get('step');
    const forcedStep = stepParam ? parseInt(stepParam) : undefined;
    
    if (weighingId && !loadedWeighingId) {
      loadExistingWeighing(weighingId, forcedStep);
    }
  }, [searchParams, loadedWeighingId]);

  // Récupérer les détails de la pesée quand on arrive à l'étape 3 ou 4
  useEffect(() => {
    if ((workflowState.currentStep === 3 || workflowState.currentStep === 4) && (workflowState.weighingId || currentWeighing?.id)) {
      const weighingId = workflowState.weighingId || currentWeighing?.id;
      if (weighingId) {
        fetchWeighingDetails(weighingId.toString());
        fetchQualityAnalysis(weighingId.toString());
      }
    }
  }, [workflowState.currentStep, workflowState.weighingId, currentWeighing?.id]);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await weighingService.getProduits();
      setProducts(response.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const loadConditionnements = async () => {
    try {
      setConditionnementsLoading(true);
      const response = await weighingService.getConditionnements();
      setConditionnements(response.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des conditionnements:', error);
    } finally {
      setConditionnementsLoading(false);
    }
  };

  // Fonction pour récupérer le nombre de pesées en attente
  const loadPendingWeighingsCount = async () => {
    try {
    
      // Approche 2: Récupérer toutes les pesées et filtrer côté client
      const allWeighingsResponse = await weighingService.getPesées({
        page_size: 1000 // Récupérer un grand nombre pour avoir toutes les pesées
      });
      
      if (allWeighingsResponse?.results && Array.isArray(allWeighingsResponse.results)) {
        const pendingWeighings = allWeighingsResponse.results.filter((weighing: any) => {
          const isPending = ['EN_ATTENTE_TARE', 'BRUT_ENREGISTRE', 'EN_COURS'].includes(weighing.statut);
          if (isPending) {
            console.log(`📋 Pesée en attente trouvée: ${weighing.reference || weighing.id} - Statut: ${weighing.statut}`);
          }
          return isPending;
        });
        
        setPendingWeighingsCount(pendingWeighings.length);
        
      } else {
        setPendingWeighingsCount(0);
      }
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement du nombre de pesées en attente:', error);
      setPendingWeighingsCount(0);
    }
  };




  // Gérer les changements d'input pour le formulaire de destination
  const handleDestinationInputChange = (field: string, value: any) => {
    setDestinationFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Synchroniser aussi avec formData pour la sauvegarde
    updateFormData(field as keyof typeof formData, value);
  };


  const handleInputChange = (field: string, value: any) => {
    updateFormData(field as any, value);
  };

  // Fonction locale pour vérifier si on peut passer à l'étape suivante (prend en compte les pesées chargées)
  const canProceedToNextStepLocal = (step: number) => {
    switch (step) {
      case 1:
        // Pour l'étape 1, vérifier si on a un poids brut (nouveau ou chargé) ET les champs requis
        const hasGrossWeight = !!(workflowState.grossWeight || currentWeighing?.poids_brut);
        const hasRequiredFields = !!(formData.vehicule && formData.produit);
        
      
        
        return hasGrossWeight && hasRequiredFields;
      case 2:
        return !!workflowState.analysisData;
      case 3:
        return !!workflowState.tareWeight;
      default:
        return false;
    }
  };

  const handleWeightCapture = async (weightData: any) => {
    const weight = weightData.weight || weightData;
    const weighingData = await saveGrossWeight(weight);
    if (weighingData) {
      // Mettre à jour le poids actuel affiché dans le header
      setCurrentWeight(weight);
      // Mettre à jour currentWeighing avec les détails de la pesée créée
      if (weighingData.id) {
        setLoadedWeighingId(weighingData.id.toString());
        // Le contexte sera mis à jour automatiquement par useWeighingWorkflow
      }
      return true; // Indiquer le succès
    } else {
      // Afficher un message d'erreur détaillé
      const errorMessage = workflowError || 'Échec de l\'enregistrement du poids brut';
      console.error('❌ Échec de l\'enregistrement du poids brut:', errorMessage);
      return false; // Indiquer l'échec
    }
  };

  const handlePontBasculeChange = (pontBasculeId: string) => {
    setSelectedPontBasculeId(pontBasculeId);
    // Recharger les informations du nouveau pont bascule
    fetchPontBasculeInfo(pontBasculeId);
  };


  // Configuration des étapes dynamique selon le processus
  const steps: WeighingStep[] = [
    {
      id: 1,
      title: getStepLabel(1, weighingProcess),
      status: workflowState.currentStep === 1 ? 'active' : 
              workflowState.currentStep > 1 ? 'completed' : 'pending'
    },
    {
      id: 2,
      title: getStepLabel(2, weighingProcess),
      status: workflowState.currentStep === 2 ? 'active' : 
              workflowState.currentStep > 2 ? 'completed' : 'pending'
    },
    {
      id: 3,
      title: getStepLabel(3, weighingProcess),
      status: workflowState.currentStep === 3 ? 'active' : 
              workflowState.currentStep > 3 ? 'completed' : 'pending'
    },
    {
      id: 4,
      title: getStepLabel(4, weighingProcess),
      status: workflowState.currentStep === 4 ? 'active' : 'pending'
    }
  ];

  const handleNextStep = async () => {
   
    if (workflowState.currentStep === 1) {
      // Vérifier que les champs obligatoires sont remplis
      const hasGrossWeight = !!(workflowState.grossWeight || currentWeighing?.poids_brut);
      const hasRequiredFields = !!(formData.vehicule && formData.produit);
      
      
      if (!hasRequiredFields || !hasGrossWeight) {
        console.log('❌ Impossible de passer à l\'étape suivante - champs manquants');
        return;
      }
      
    
      // Toujours passer à l'étape 2 (analyse qualité)
      nextStep();
    } else if (workflowState.currentStep === 2) {
     
      // L'analyse qualité sera gérée par le composant QualityPage
      // On passe directement à l'étape suivante
      nextStep();
    } else if (workflowState.currentStep === 3) {
     
      // La pesée tare sera gérée par le composant WeightCapture
      nextStep();
    }
  };

  const handlePrevStep = () => {

    previousStep();
    
    // Synchroniser les données après le retour à l'étape précédente
    if (currentWeighing && loadedWeighingId) {
      setTimeout(() => {
       
        syncWeighingDataToForm(currentWeighing);
      }, 300); // Délai plus long pour s'assurer que l'étape a changé et les composants sont prêts
    }
  };

  const handleStepClick = (stepId: number) => {
    // Permettre la navigation vers les étapes précédentes
    if (stepId < workflowState.currentStep) {
      
      goToStep(stepId);
      
      // Synchroniser les données après le changement d'étape
      if (currentWeighing && loadedWeighingId) {
        setTimeout(() => {
          
          syncWeighingDataToForm(currentWeighing);
        }, 300); // Délai plus long pour s'assurer que l'étape a changé et les composants sont prêts
      }
    }
  };

  const handleTareWeightCapture = async (weightData: any) => {
    const weight = weightData.weight || weightData;
    const success = await saveTareWeight(weight);
    if (success) {
      // Mettre à jour le poids actuel affiché dans le header
      setCurrentWeight(weight);
      
      // Calcul du poids net non utilisé ici; il est calculé lors de la sauvegarde complète
      
    }
  };

  // Fonction pour sauvegarder complètement le poids tare avec toutes les données de l'étape 3
  const handleSaveTareWeightComplete = async (tareWeight: number) => {
    try {
     
      // Calculer le poids net
      const grossWeight = currentWeighing?.poids_brut || workflowState.grossWeight;
      const netWeight = grossWeight ? parseFloat(grossWeight.toString()) - tareWeight : 0;
      
      // Récupérer les données des formulaires de l'étape 3
      const destinationData = destinationFormData;
      const detailData = formData; // Données du formulaire de détails
      
      // Préparer les données à sauvegarder pour l'étape 3
      const updateData: any = {
        // Poids - convertir en chaînes pour les DecimalField
        poids_tare: tareWeight.toString(),
        poids_net: netWeight.toString(),
        
        // Données de destination/stockage (onglet 1)
        destination: destinationData.destination,
        numero_lot: destinationData.numeroLot,
        
        // Observations
        observations: detailData.observations,
        
        // Champs d'analyse qualité - Utiliser les données de formData
        nb_sacs_declares: detailData.nbSacsDeclares || 0,
        poids_declares: (detailData.poidsDeclares || 0).toString(),
        nb_sacs_sains: detailData.nbSacsSains || 0,
        nb_sacs_recond: detailData.nbSacsRecond || 0,
        nb_sacs_humides: detailData.nbSacsHumides || 0,
        nb_sacs_dechires: detailData.nbSacsDechires || 0,
        nb_sacs_rejetes: detailData.nbSacsRejetes || 0,
        nb_sacs_acceptes: detailData.nbSacsAcceptes || 0,
        poids_acceptes: (detailData.poidsAcceptes || 0).toString(),
        
        // Statut sera mis à jour plus tard dans handleTareSummaryTerminer
        // statut: 'TARE_ENREGISTREE'
      };
      
      // Ajouter magasin seulement s'il est défini
      if (destinationData.magasin && destinationData.magasin !== '') {
        updateData.magasin = parseInt(destinationData.magasin);
      }
      // TEMPORAIRE: Ne pas envoyer le champ magasin pour tester
      // if (destinationData.magasin && destinationData.magasin !== '') {
      //   updateData.magasin = parseInt(destinationData.magasin);
      // }
      // Si aucun magasin n'est sélectionné, ne pas inclure le champ dans updateData
      
      // Appel API pour mettre à jour la pesée
      if (currentWeighing?.id) {
       
        
        await weighingService.updatePesee(parseInt(currentWeighing.id), updateData);
        
      
        
        // Mettre à jour le contexte avec les nouvelles données
        updateWeighing(updateData);
        
        // Préparer les données pour le modal de récapitulatif
        const summaryData = {
          reference: currentWeighing?.reference,
          poids_brut: grossWeight,
          poids_tare: tareWeight,
          poids_net: netWeight,
          destination: destinationData.destination,
          numero_lot: destinationData.numeroLot,
          provenance: detailData.provenance,
          connaissement: detailData.connaissement,
          numero_transfert: detailData.numeroTransfert,
          sticker: detailData.sticker,
          observations: detailData.observations,
          // Champs d'analyse qualité
          nb_sacs_declares: detailData.nbSacsDeclares || 0,
          poids_declares: detailData.poidsDeclares || 0,
          nb_sacs_sains: detailData.nbSacsSains || 0,
          nb_sacs_recond: detailData.nbSacsRecond || 0,
          nb_sacs_humides: detailData.nbSacsHumides || 0,
          nb_sacs_dechires: detailData.nbSacsDechires || 0,
          nb_sacs_rejetes: detailData.nbSacsRejetes || 0,
          nb_sacs_acceptes: detailData.nbSacsAcceptes || 0,
          poids_acceptes: detailData.poidsAcceptes || 0,
          produit: currentWeighing?.produit_info?.nom || currentWeighing?.produit,
          vehicule: currentWeighing?.camion_info?.immatriculation || currentWeighing?.camion,
          chauffeur: currentWeighing?.chauffeur_info ? `${currentWeighing.chauffeur_info.nom} ${currentWeighing.chauffeur_info.prenom}` : currentWeighing?.chauffeur,
          fournisseur: currentWeighing?.fournisseur_info?.nom || currentWeighing?.fournisseur,
          client: currentWeighing?.client_info?.nom || currentWeighing?.client
        };
        
        setTareSummaryData(summaryData);
        setShowTareSummaryModal(true);
      }
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde de la tare:', error);
      
      // Afficher les détails de l'erreur
      if (error.response) {
        console.error('Détails de la réponse:', error.response.data);
        console.error('Status:', error.response.status);
        alert(`Erreur ${error.response.status}: ${JSON.stringify(error.response.data)}`);
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        alert('Erreur de connexion. Vérifiez votre connexion réseau.');
      } else {
        console.error('Erreur:', error.message);
        alert(`Erreur: ${error.message}`);
      }
    }
  };


  // Fonctions pour gérer les actions du modal de récapitulatif
  const handleTareSummaryReprendre = async () => {
    try {
      
      // Annuler l'enregistrement de l'étape 3 - remettre les données à leur état précédent
      if (currentWeighing?.id) {
        const cancelData = {
          // Remettre le statut à l'étape précédente
          statut: 'EN_ATTENTE_TARE' as const,
          // Annuler les données de l'étape 3
          poids_tare: undefined,
          poids_net: undefined,
          destination: '',
          numero_lot: '',
          magasin: undefined,
          observations: '',
          // Remettre les compteurs d'analyse qualité à zéro
          nb_sacs_declares: 0,
          nb_sacs_sains: 0,
          nb_sacs_recond: 0,
          nb_sacs_humides: 0,
          nb_sacs_dechires: 0
        };
        
        await weighingService.updatePesee(parseInt(currentWeighing.id), cancelData);
        
        // Mettre à jour le contexte avec les données annulées
        updateWeighing(cancelData);
      }
      
      // Fermer le modal et rester sur l'étape 3
      setShowTareSummaryModal(false);
      setTareSummaryData(null);
    } catch (error) {
      alert('Erreur lors de l\'annulation. Veuillez réessayer.');
    }
  };

  const handleTareSummaryTerminer = async () => {
    try {
      // Finaliser la pesée - marquer comme complétée
      if (currentWeighing?.id) {
        // Inclure le poids tare car le serializer l'exige pour COMPLETEE
        const updateData: any = {
          statut: 'COMPLETEE'
        };
        
        // Ajouter le poids tare s'il est disponible
        if (currentWeighing.poids_tare) {
          updateData.poids_tare = currentWeighing.poids_tare;
        }
        
        // Ajouter le poids net calculé
        if (currentWeighing.poids_brut && currentWeighing.poids_tare) {
          updateData.poids_net = parseFloat(currentWeighing.poids_brut.toString()) - parseFloat(currentWeighing.poids_tare.toString());
        }
        await weighingService.updatePesee(parseInt(currentWeighing.id), updateData);
        
        // Mettre à jour le contexte avec le statut final
        updateWeighing({ statut: 'COMPLETEE' });
      } else {
        alert('Aucune pesée active à finaliser. Veuillez créer une nouvelle pesée.');
        return;
      }
      
      // Fermer le modal
      setShowTareSummaryModal(false);
      setTareSummaryData(null);
      
      // Passer à l'étape 4 (impression/envoi du reçu)
      nextStep();
      
    } catch (error) {
      alert('Erreur lors de la finalisation. Veuillez réessayer.');
    }
  };

  // Gérer le succès de l'analyse qualité
  const handleQualityAnalysisSuccess = () => {
    // L'analyse qualité est maintenant gérée par la modale dans QualityPage
    // On passe directement à l'étape suivante
    nextStep();
  };

  // Fonction pour annuler une pesée
  const handleCancelWeighing = () => {
    handleNewWeighing('standard'); // Utilise le processus standard par défaut
  };

  // Fonction pour reprendre une pesée en attente
  const handleSelectPendingWeighing = (weighing: Pesée & { targetStep?: number }) => {
    // Charger la pesée existante avec l'étape cible
    if (weighing.id) {
      loadExistingWeighing(weighing.id.toString(), weighing.targetStep);
    }
  };

  // Fonction pour commencer une nouvelle pesée
  const handleNewWeighing = (selectedProcess: WeighingProcess = 'standard') => {
    
    // Réinitialiser le poids actuel
    setCurrentWeight(null);
    
    // Réinitialiser le statut du pont bascule
    setPontBasculeStatus('checking');
    
    // Réinitialiser l'ID de la pesée chargée
    setLoadedWeighingId(null);
    
    // Réinitialiser la pesée actuelle
    setCurrentWeighing(null);
    
    // Réinitialiser les données du formulaire de destination
    setDestinationFormData({
      destination: '',
      magasin: '',
      numeroLot: ''
    });
    
    // Réinitialiser l'analyse qualité
    setQualityAnalysis(null);
    
    
    // Fermer tous les modals
    setShowWeighingDetailsModal(false);
    setShowTareSummaryModal(false);
    setTareSummaryData(null);
    
    // Réinitialiser le workflow et retourner à l'étape 1
    goToStep(1);
    
    // Initialiser le processus de pesée avec le processus choisi
    initializeWeighingProcess(selectedProcess);
  };


  const handlePrint = () => {
    console.log('✅ Impression du ticket lancée');
  };

  const handleExport = () => {
    console.log('✅ Export PDF du ticket lancé');
  };

  const handleEmail = () => {
    console.log('✅ Email du ticket lancé');
  };


  // Fonction pour commencer une nouvelle pesée (remplace handleCompleteWeighing)
  const handleStartNewWeighing = () => {
    // Demander confirmation à l'utilisateur
    const confirmNew = window.confirm(
      'Voulez-vous commencer une nouvelle pesée ? La pesée actuelle est déjà complétée.'
    );

    if (!confirmNew) {
      return;
    }

    // Réinitialiser complètement la page pour une nouvelle pesée
    handleNewWeighing('standard');
  };

  // Fonction pour archiver la pesée
  const handleArchiveWeighing = async (shouldArchive: boolean) => {
    try {
      if (!currentWeighing?.id) {
        alert('Aucune pesée à archiver');
        return;
      }
      if (shouldArchive) {
        // Appeler le service pour archiver la pesée
        const response = await weighingService.archiveWeighing(
          parseInt(currentWeighing.id),
          'Archivage manuel depuis l\'interface'
        );

        if (response.success) {
          alert('Pesée archivée avec succès !');
          
          // Réinitialiser complètement la page pour une nouvelle pesée
          window.location.reload();
        } else {
          throw new Error(response.message || 'Erreur lors de l\'archivage');
        }
      }

    } catch (error) {
      alert(`Erreur lors de l'archivage de la pesée: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  // Le calcul du poids net est maintenant fait directement dans getTicketData()


  // Données du ticket pour l'étape 4
  const getTicketData = (): WeighingTicketData => {
    
    return {
    id: currentWeighing?.id?.toString() || workflowState.weighingId || '',
    code: currentWeighing?.reference || workflowState.weighingCode || '',
    grossWeight: currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()) : (workflowState.grossWeight || 0),
    tareWeight: currentWeighing?.poids_tare ? parseFloat(currentWeighing.poids_tare.toString()) : (workflowState.tareWeight || 0),
    netWeight: currentWeighing?.poids_net ? parseFloat(currentWeighing.poids_net.toString()) : (() => {
      const gross = currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()) : (workflowState.grossWeight || 0);
      const tare = currentWeighing?.poids_tare ? parseFloat(currentWeighing.poids_tare.toString()) : (workflowState.tareWeight || 0);
      return gross - tare;
    })(),
    vehicle: currentWeighing?.camion_info?.immatriculation || currentWeighing?.camion || formData.vehicule,
    driver: currentWeighing?.chauffeur_info ? `${currentWeighing.chauffeur_info.nom} ${currentWeighing.chauffeur_info.prenom}` : currentWeighing?.chauffeur || formData.chauffeur,
    product: currentWeighing?.produit_info?.nom || currentWeighing?.produit || formData.produit,
    site: siteInfo?.nom || pontBasculeInfo?.site || 'Site principal',
    tenant: tenantInfo?.nom || pontBasculeInfo?.tenant || 'Tenant principal',
    grossAt: currentWeighing?.date_heure || new Date().toISOString(),
    tareAt: currentWeighing?.updated_at || new Date().toISOString(),
    grossUser: currentWeighing?.agent_premiere_pesee_nom || currentWeighing?.agent_premiere_pesee || 'Agent non spécifié',
    tareUser: currentWeighing?.agent_deuxieme_pesee_nom || currentWeighing?.agent_deuxieme_pesee || 'Agent non spécifié',
    analysisUser: currentWeighing?.agent_analyse_qualite_nom || currentWeighing?.agent_analyse_qualite || 'Agent non spécifié',
    observations: currentWeighing?.observations || formData.observations,
    
    // NOUVEAUX CHAMPS POUR LE TICKET CAJOU
    
    // En-tête du ticket
    weighingDate: currentWeighing?.date_heure ? new Date(currentWeighing.date_heure).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'),
    campaignYear: new Date().getFullYear().toString(),
    ticketNumber: currentWeighing?.reference || 'NUMEROTICK',
    
    // Section Fournisseur
    supplierIdentity: currentWeighing?.fournisseur_info?.nom || currentWeighing?.fournisseur || 'NOM DE L\'ENTITE',
    clientCode: currentWeighing?.client_info?.nom || currentWeighing?.client || 'XXXX-XXXX',
    exporterCode: currentWeighing?.exportateur_info?.nom || currentWeighing?.exportateur || '01',
    productNature: currentWeighing?.produit_info?.type_display || currentWeighing?.produit_info?.type || '',
    productOrigin: currentWeighing?.provenance || '',
    bushLotNumber: currentWeighing?.numero_lot || '',
    
    // Section Identification
    trailerRegistration: currentWeighing?.remorque_info?.immatriculation || currentWeighing?.remorque || formData.remorque || '',
    transferSheetNumber: currentWeighing?.numero_transfert || formData.numeroTransfert || 'N° FICHE',
    transporterName: currentWeighing?.transporteur_info?.nom || currentWeighing?.transporteur || formData.transporteur || '',
    destination: currentWeighing?.destination || formData.destination || '',
    provenance: currentWeighing?.provenance || formData.provenance || '',
    stickerNumber: currentWeighing?.sticker || formData.sticker || 'N° STICKER',
    
    // Section Poids et Sacherie
    firstWeighingWeight: currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()) : undefined,
    firstWeighingDateTime: currentWeighing?.date_heure || undefined,
    secondWeighingWeight: currentWeighing?.poids_tare ? parseFloat(currentWeighing.poids_tare.toString()) : undefined,
    secondWeighingDateTime: currentWeighing?.updated_at || undefined,
    humidityRateKOR: qualityAnalysis?.moisture ? `${qualityAnalysis.moisture}% KOR : ${qualityAnalysis.moisture}` : 'VALEUR % KOR : Valeur KOR',
    qualityRefraction: qualityAnalysis?.quality_numbers ? (parseInt(qualityAnalysis.quality_numbers.split('/')[0]) || 0) : 0,
    packagingType: currentWeighing?.produit_info?.conditionnement?.toString() || 'SACS 50KG', // Type de conditionnement du produit
    humidityRefraction: qualityAnalysis?.moisture || undefined,
    bagWeight: 50, // Poids standard d'un sac
    
    // Comptage des sacs étendu - Utiliser les données de la pesée actuelle
    sacsCount: (() => {
      const sacsData = {
        nb_sacs_declares: currentWeighing?.nb_sacs_declares || formData.nbSacsDeclares || 0,
        poids_declares: currentWeighing?.poids_declares || formData.poidsDeclares || 0,
        nb_sacs_sains: currentWeighing?.nb_sacs_sains || formData.nbSacsSains || 0,
        nb_sacs_recond: currentWeighing?.nb_sacs_recond || formData.nbSacsRecond || 0,
        nb_sacs_humides: currentWeighing?.nb_sacs_humides || formData.nbSacsHumides || 0,
        nb_sacs_dechires: currentWeighing?.nb_sacs_dechires || formData.nbSacsDechires || 0,
        nb_sacs_rejetes: currentWeighing?.nb_sacs_rejetes || formData.nbSacsRejetes || 0,
        nb_sacs_acceptes: currentWeighing?.nb_sacs_acceptes || formData.nbSacsAcceptes || 0,
        poids_acceptes: currentWeighing?.poids_acceptes || formData.poidsAcceptes || 0
      };
      
      return sacsData;
    })(),
    
    // Données d'analyse qualité
    qualityAnalysis: qualityAnalysis ? {
      quality_decision: qualityAnalysis.quality_decision,
      quality_numbers: qualityAnalysis.quality_numbers,
      moisture: qualityAnalysis.moisture,
      proteins: qualityAnalysis.proteins,
      hectoliter_weight: qualityAnalysis.hectoliter_weight,
      impurities: qualityAnalysis.impurities,
      broken_grains: qualityAnalysis.broken_grains,
      germinated_grains: qualityAnalysis.germinated_grains,
      observations: qualityAnalysis.observations
    } : undefined,
    
    // Signatures
    weighingAgentSignature: 'Agent Peseur',
    clientSupplierSignature: 'Client / Fournisseur',
    
    // Analyse qualité complète
    analyse_qualite: currentWeighing?.analyse_qualite ? {
      grainnage: currentWeighing.analyse_qualite.grainnage,
      humidite: currentWeighing.analyse_qualite.humidite,
      quality: currentWeighing.analyse_qualite.quality,
      quality_level: currentWeighing.analyse_qualite.quality_level,
      quality_decision: currentWeighing.analyse_qualite.quality_decision,
      quality_score: currentWeighing.analyse_qualite.quality_score,
      observations: currentWeighing.analyse_qualite.observations,
      analysed_by_username: currentWeighing.analyse_qualite.analysed_by_username,
      analysed_at: currentWeighing.analyse_qualite.analysed_at
    } : undefined
    };
  };

  // Préparer les options des produits pour le SearchableSelect
  const productOptions = products.map(product => ({
    value: product.id.toString(),
    label: `${product.nom} - ${product.type_display || product.type || ''}`
  }));

  // Préparer les options des conditionnements pour le SearchableSelect
  const conditionnementOptions = conditionnements.map(conditionnement => ({
    value: conditionnement.id.toString(),
    label: `${conditionnement.nom} - ${conditionnement.poids || ''}kg`
  }));


  return (
    <PermissionGuard 
      permission={PERMISSIONS.WEIGHING_VIEW}
      showError={true}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Informations de la pesée actuelle */}
        <CurrentWeighingInfo className="m-4" />
      
      {/* Header simplifié */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="p-2 text-gray-400 hover:text-gray-600 mr-4">
              <i className="fas fa-arrow-left"></i>
            </button>
        <HeaderTabs 
          onNewWeighing={handleNewWeighing}
          disabled={workflowState.currentStep > 1 && !currentWeighing}
          defaultProcess={workflowState.weighingProcess}
        />
        
      
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-6">
              {/* État du pont bascule */}
              
              
              {/* Poids actuel */}
              <div>
                <p className="text-sm text-gray-600">Poids actuel</p>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-green-600 mr-2">
                    {currentWeight ? `${currentWeight.toLocaleString()} kg` : '-- kg'}
                  </span>
                  <div className={`w-3 h-3 rounded-full ${currentWeight ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stepper avec indicateur de pesée en modification */}
        <div className="mb-6">
         
          
          <WeighingStepper
            steps={steps}
            currentStep={workflowState.currentStep}
            onStepClick={handleStepClick}
            className="mb-6"
          />
        </div>

        {/* Messages d'erreur */}
        {workflowError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{workflowError}</p>
          </div>
        )}

        {/* Indicateur de chargement d'une pesée existante - Masque tout le contenu */}
        {isLoadingExistingWeighing ? (
          <div className="fixed inset-0 bg-white z-[5000] flex items-center justify-center">
            <div className="text-center">
              <div className="relative mb-8">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 mx-auto"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-1/2 transform -translate-x-1/2"></div>
              </div>
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-blue-800">
                  Redirection en cours...
                </h2>
                <p className="text-lg text-blue-600 max-w-md mx-auto">
                  {loadingStep || 'Chargement de la pesée existante et redirection vers l\'étape appropriée'}
                </p>
                <div className="flex justify-center space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
                <div className="w-64 bg-blue-200 rounded-full h-3 mx-auto">
                  <div className="bg-blue-600 h-3 rounded-full animate-pulse" style={{width: '60%'}}></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Notification de pesée chargée */}
            {showWeighingLoadedNotification && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <i className="fas fa-edit text-blue-600 mr-3 text-lg"></i>
                    <div>
                      <p className="text-sm text-blue-800 font-medium">
                        Mode modification - Pesée chargée avec succès !
                      </p>
                      <p className="text-xs text-blue-600">
                        Référence: {currentWeighing?.reference} - Étape {workflowState.currentStep} - Toutes les données sont pré-remplies
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowWeighingLoadedNotification(false)}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {workflowState.currentStep === 1 && `${getStepLabel(1, weighingProcess)} - Étape 1/4`}
                    {workflowState.currentStep === 2 && `${getStepLabel(2, weighingProcess)} - Étape 2/4`}
                    {workflowState.currentStep === 3 && `${getStepLabel(3, weighingProcess)} - Étape 3/4`}
                    {workflowState.currentStep === 4 && `${getStepLabel(4, weighingProcess)} - Étape 4/4`}
                  </h2>
                  
              
                </div>
                
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  workflowState.currentStep === 1 ? 'bg-green-100 text-green-800' :
                  workflowState.currentStep === 2 ? 'bg-blue-100 text-blue-800' :
                  workflowState.currentStep === 3 ? 'bg-purple-100 text-purple-800' :
                  'bg-orange-100 text-orange-800'
                }`}>
                  Étape {workflowState.currentStep}/4
                </span>
              </div>

              {workflowState.currentStep === 1 && (
                // Étape 1: Formulaire avec onglets
                <div className="space-y-6">
                  {/* Type de Pesée, Produit et Conditionnement */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <SearchableSelect
                        value={formData.typePesee}
                        onChange={(value) => handleInputChange('typePesee', value)}
                        options={[
                          { value: 'reception', label: 'Réception' },
                          { value: 'expedition', label: 'Expédition' },
                          { value: 'transfert', label: 'Transfert' }
                        ]}
                        placeholder="Sélectionner le type"
                        label="Type *"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <SearchableSelect
                        value={formData.produit}
                        onChange={(value) => handleInputChange('produit', value)}
                        options={productOptions}
                        placeholder={productsLoading ? "Chargement..." : "Sélectionner un produit"}
                        label="PRODUIT *"
                        className="w-full"
                        disabled={productsLoading}
                      />
                      {productsLoading && (
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                          Chargement des produits...
                        </div>
                      )}
                    </div>
                    <div>
                      <SearchableSelect
                        value={formData.conditionnement}
                        onChange={(value) => handleInputChange('conditionnement', value)}
                        options={conditionnementOptions}
                        placeholder={conditionnementsLoading ? "Chargement..." : "Sélectionner un conditionnement"}
                        label="SAC/CONDITIONNEMENT"
                        className="w-full"
                        disabled={conditionnementsLoading}
                      />
                      {conditionnementsLoading && (
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500 mr-2"></div>
                          Chargement des conditionnements...
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section unifiée - Informations Véhicule et Acteurs */}
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 shadow-lg mb-4">
                    <div className="p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Colonne gauche - Informations Véhicule */}
                        <div>
                          <VehicleInfoSection 
                            formData={{
                              vehicule: formData.vehicule,
                              remorque: formData.remorque,
                              transporteur: formData.transporteur,
                              chauffeur: formData.chauffeur,
                              convoyeur: formData.convoyeur
                            }}
                            onInputChange={handleInputChange}
                          />
                        </div>
                        
                        {/* Colonne droite - Informations Acteurs */}
                        <div>
                          <ClientSupplierSection 
                            formData={{
                              client: formData.client,
                              fournisseur: formData.fournisseur,
                              exportateur: formData.exportateur
                            }}
                            onInputChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Section unifiée - Déclarations et Documents */}
                  <div className="bg-white border border-orange-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Colonne gauche - Champs de déclaration */}
                      <div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nb de sacs déclarés
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={formData.nbSacsDeclares || ''}
                              onChange={(e) => handleInputChange('nbSacsDeclares', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              placeholder="Nombre de sacs déclarés"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Poids déclarés (kg)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formData.poidsDeclares || ''}
                              onChange={(e) => handleInputChange('poidsDeclares', parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              placeholder="Poids déclaré en kg"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne droite - Documents et Références */}
                      <div>
                        <TransferStickerSection 
                          formData={{
                            numeroTransfert: formData.numeroTransfert,
                            sticker: formData.sticker
                          }}
                          onInputChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Capture du poids - Intégré avec sélection de pont bascule */}
                  <WeightCapture
                    siteId={user?.sites?.[0]?.id}
                    onWeightCaptured={handleWeightCapture}
                    onWeightChange={setCurrentWeight}
                    onStatusChange={setPontBasculeStatus}
                    onPontBasculeChange={handlePontBasculeChange}
                    mode={getCurrentWeighingType(1, weighingProcess) === 'tare' ? 'tare' : 'gross'}
                    hasExistingWeight={(() => {
                      const hasWeight = !!(workflowState.grossWeight || currentWeighing?.poids_brut);
                      return hasWeight;
                    })()}
                    existingWeight={currentWeighing?.poids_brut || workflowState.grossWeight}
                    className="mb-6"
                  />

                  {/* Affichage du code de pesée si créé */}
                  {workflowState.weighingCode && (
                    <WeighingCodeDisplay
                      weighingCode={workflowState.weighingCode}
                      weighingId={workflowState.weighingId}
                      tenantId={workflowState.tenantId}
                      siteId={workflowState.siteId}
                      className="mb-6"
                    />
                  )}

                  {/* Boutons d'action */}
                  <div className="flex justify-between pt-4">
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_CANCEL}
                      onClick={handleCancelWeighing}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      disabledMessage="Permission insuffisante pour annuler une pesée"
                    >
                      <i className="fas fa-times mr-2"></i>Annuler
                    </PermissionButton>
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_CREATE}
                      onClick={() => {
                        handleNextStep();
                      }}
                      disabled={!canProceedToNextStepLocal(1)}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabledMessage="Permission insuffisante pour créer une pesée"
                    >
                      <i className="fas fa-arrow-right mr-2"></i>Étape suivante
                    </PermissionButton>
                  </div>
                </div>
              )}

                {workflowState.currentStep === 2 && (
                  <div className="space-y-6">
                    <QualityPage
                      currentWeighing={currentWeighing || undefined}
                      onNextStep={handleQualityAnalysisSuccess}
                      onPrevStep={handlePrevStep}
                    />
                  </div>
                )}

               {workflowState.currentStep === 3 && (
                // Étape 3: Poids Tare avec onglets pour destination et détails
                <div className="space-y-6">
                  {/* Section unifiée - Informations de la pesée et analyse qualité */}
                  <div className="bg-blue-50 rounded-lg p-6 mb-6 shadow-md">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <i className="fas fa-info-circle text-blue-600 mr-3 text-xl"></i>
                        <h3 className="text-xl font-semibold text-gray-900">Informations de la pesée</h3>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm font-medium">
                        #{currentWeighing?.reference || 'N/A'}
                      </span>
                    </div>
                    
                    {/* Grille des informations principales */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {/* Produit */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-box text-orange-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Produit</span>
                        </div>
                        <p className="text-orange-600 font-semibold text-base">
                          {currentWeighing?.produit_info?.nom || currentWeighing?.produit || formData.produit || ''}
                        </p>
                      </div>

                      {/* Véhicule */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-truck text-green-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Véhicule</span>
                        </div>
                        <p className="text-green-600 font-semibold text-base">
                          {currentWeighing?.camion_info?.immatriculation || currentWeighing?.camion || formData.vehicule || 'N/A'}
                        </p>
                      </div>

                      {/* Poids brut */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-weight text-purple-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Poids brut</span>
                        </div>
                        <p className="text-purple-600 font-semibold text-base">
                          {currentWeighing?.poids_brut ? parseFloat(currentWeighing.poids_brut.toString()).toLocaleString() : (workflowState.grossWeight ? parseFloat(workflowState.grossWeight.toString()).toLocaleString() : 'N/A')} kg
                        </p>
                      </div>

                      {/* Date pesée */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-2">
                          <i className="fas fa-calendar text-purple-500 mr-2"></i>
                          <span className="text-sm text-gray-600">Date pesée</span>
                        </div>
                        <p className="text-purple-600 font-semibold text-base">
                          {currentWeighing?.date_heure ? new Date(currentWeighing.date_heure).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    {/* Bouton pour afficher toutes les informations */}
                    <div className="flex justify-center mt-4">
                      <button
                        onClick={() => setShowWeighingDetailsModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors border border-blue-200"
                        title="Voir toutes les informations de la pesée"
                      >
                        <i className="fas fa-info-circle mr-2"></i>
                        <span className="text-sm font-medium">Détails complets</span>
                      </button>
                    </div>




                  </div>
                   
                    {/* Section d'analyse qualité des sacs */}
                  <div className="mt-6">
                    <QualityAnalysisSection 
                      formData={{
                        nbSacsDeclares: formData.nbSacsDeclares || 0,
                        nbSacsSains: formData.nbSacsSains || 0,
                        nbSacsRecond: formData.nbSacsRecond || 0,
                        nbSacsHumides: formData.nbSacsHumides || 0,
                        nbSacsDechires: formData.nbSacsDechires || 0,
                        nbSacsRejetes: formData.nbSacsRejetes || 0,
                        nbSacsAcceptes: formData.nbSacsAcceptes || 0,
                        poidsAcceptes: formData.poidsAcceptes || 0,
                        observations: formData.observations || ''
                      }}
                      onInputChange={handleInputChange}
                      conditionnementCapacite={currentWeighing?.conditionnement_info?.capacite || 1}
                    />
                  </div>

                  {/* Capture du poids - Intégré avec sélection de pont bascule */}
                  <WeightCapture
                    siteId={user?.sites?.[0]?.id}
                    onWeightCaptured={handleTareWeightCapture}
                    onWeightChange={setCurrentWeight}
                    onStatusChange={setPontBasculeStatus}
                    mode={getCurrentWeighingType(3, weighingProcess) === 'tare' ? 'tare' : 'gross'}
                    grossWeight={currentWeighing?.poids_brut || workflowState.grossWeight}
                    calculatedBagWeight={formData.poidsAcceptes || 0}
                    onSaveTareWeight={handleSaveTareWeightComplete}
                    hasExistingWeight={!!currentWeighing?.poids_tare}
                    className="mb-6"
                  />



                  {/* Boutons d'action */}
                  <div className="flex justify-between">
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_EDIT}
                      onClick={handlePrevStep}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      disabledMessage="Permission insuffisante pour modifier une pesée"
                    >
                      <i className="fas fa-arrow-left mr-2"></i>Retour étape 2
                    </PermissionButton>
                    
                    <PermissionButton 
                      permission={PERMISSIONS.WEIGHING_COMPLETE}
                      onClick={handleNextStep}
                      disabled={!canProceedToNextStep(3)}
                      className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabledMessage="Permission insuffisante pour compléter une pesée"
                    >
                      <i className="fas fa-arrow-right mr-2"></i>Voir le ticket
                    </PermissionButton>
                  </div>
                </div>
              )}

              {workflowState.currentStep === 4 && (
                // Étape 4: Ticket final
                <div>
                  <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">Ticket de Pesée</h3>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        Pesée terminée
                      </span>
                    </div>
                    
                    {/* Informations complètes du ticket */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">Résumé complet de la pesée</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        {/* Informations générales */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800 border-b border-gray-300 pb-1">Informations générales</h5>
                      <div>
                            <span className="text-gray-600">Référence:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().code}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Date:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().weighingDate || new Date().toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Année campagne:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().campaignYear}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Produit:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().product}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Nature produit:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().productNature || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Origine:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().productOrigin || 'N/A'}</p>
                          </div>
                        </div>
                        
                        {/* Véhicule et transport */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800 border-b border-gray-300 pb-1">Véhicule & Transport</h5>
                          <div>
                            <span className="text-gray-600">Véhicule:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().vehicle}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Chauffeur:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().driver}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Remorque:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().trailerRegistration || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Transporteur:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().transporterName || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Provenance:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().provenance || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Destination:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().destination || 'N/A'}</p>
                        </div>
                      </div>
                      
                        {/* Poids et qualité */}
                        <div className="space-y-3">
                          <h5 className="font-medium text-gray-800 border-b border-gray-300 pb-1">Poids & Qualité</h5>
                      <div>
                            <span className="text-gray-600">Poids brut:</span>
                            <p className="font-semibold text-blue-600 text-lg">{getTicketData().grossWeight.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Poids tare:</span>
                            <p className="font-semibold text-orange-600 text-lg">{getTicketData().tareWeight.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Poids net:</span>
                            <p className="font-semibold text-green-600 text-xl">{getTicketData().netWeight.toLocaleString()} kg</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Taux humidité:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().humidityRateKOR || 'N/A'}</p>
                        </div>
                          <div>
                            <span className="text-gray-600">Réfraction qualité:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().qualityRefraction || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Type conditionnement:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().packagingType || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Informations partenaires */}
                      <div className="mt-6 pt-4 border-t border-gray-300">
                        <h5 className="font-medium text-gray-800 mb-4">Partenaires</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Fournisseur:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().supplierIdentity || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Client:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().clientCode || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Exportateur:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().exporterCode || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Documents et références */}
                      <div className="mt-6 pt-4 border-t border-gray-300">
                        <h5 className="font-medium text-gray-800 mb-4">Documents & Références</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">N° Lot:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().bushLotNumber || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">N° Transfert:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().transferSheetNumber || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">N° Sticker:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().stickerNumber || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Observations:</span>
                            <p className="font-semibold text-gray-900">{getTicketData().observations || 'Aucune'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions du ticket */}
                    <div className="flex justify-center">
                      <TicketActions
                        ticketData={getTicketData()}
                        onPrint={handlePrint}
                        onExport={handleExport}
                        onEmail={handleEmail}
                        variant="default"
                        showLabels={true}
                      />
                    </div>
                  </div>
                  
                  {/* Bouton pour commencer une nouvelle pesée */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-center space-x-4">
                      <PermissionButton
                        permission={PERMISSIONS.WEIGHING_CREATE}
                        onClick={handleStartNewWeighing}
                        className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center font-medium"
                        title="Commencer une nouvelle pesée"
                        disabledMessage="Permission insuffisante pour créer une pesée"
                      >
                        <i className="fas fa-plus-circle mr-2"></i>
                        Nouvelle pesée
                      </PermissionButton>
                      
                      <PermissionButton
                        permission={PERMISSIONS.WEIGHING_COMPLETE}
                        onClick={() => {
                          const confirmArchive = window.confirm(
                            'Êtes-vous sûr de vouloir archiver cette pesée ? Cette action verrouillera les modifications.'
                          );
                          if (confirmArchive) {
                            handleArchiveWeighing(true);
                          }
                        }}
                        className="px-8 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center font-medium"
                        title="Archiver la pesée (verrouille les modifications)"
                        disabledMessage="Permission insuffisante pour archiver une pesée"
                      >
                        <i className="fas fa-archive mr-2"></i>
                        Archiver la pesée
                      </PermissionButton>
                    </div>
                    <p className="text-center text-sm text-gray-600 mt-2">
                      La pesée est complétée. Cliquez pour commencer une nouvelle pesée ou archiver celle-ci.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

                     {/* Sidebar */}
           <div className="lg:col-span-1 space-y-4">
             {/* Bouton Pesées en attente */}
             <PermissionGuard 
               permission={PERMISSIONS.WEIGHING_VIEW}
               fallback={
                 <div className="bg-white rounded-xl shadow-sm p-6">
                   <button 
                     disabled
                     className="w-full px-4 py-3 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed"
                   >
                     <i className="fas fa-clock mr-2"></i>
                     Pesées en attente (0)
                   </button>
                 </div>
               }
             >
               <div className="bg-white rounded-xl shadow-sm p-6">
                 <button 
                   onClick={() => {
                     loadPendingWeighingsCount(); // Rafraîchir le count avant d'ouvrir
                     setShowPendingWeighingsModal(true);
                   }}
                   className="w-full px-4 py-3 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors border border-orange-200 flex items-center justify-between"
                 >
                   <div className="flex items-center">
                     <i className="fas fa-clock mr-2"></i>
                     <span className="font-medium">Pesées en attente</span>
                   </div>
                   <div className="flex items-center">
                     <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded-full text-sm font-semibold">
                       {pendingWeighingsCount}
                     </span>
                     <i className="fas fa-chevron-right ml-2"></i>
                   </div>
                 </button>
               </div>
             </PermissionGuard>

             {/* Sections Origin et Destination - Sans cartes */}
             <div className="space-y-4">
               <OriginSection 
                 formData={{
                   provenance: formData.provenance,
                   connaissement: formData.connaissement
                 }}
                 onInputChange={handleInputChange}
               />
               
               <DestinationSection 
                 formData={{
                   destination: destinationFormData.destination,
                   magasin: destinationFormData.magasin,
                   numeroLot: destinationFormData.numeroLot
                 }}
                 onInputChange={handleDestinationInputChange}
               />
             </div>

           </div>
        </div>
          </>
        )}
      </div>


      {/* Modal des détails complets de la pesée */}
      {showWeighingDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Détails complets de la pesée</h2>
                <button
                  onClick={() => setShowWeighingDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              {currentWeighing ? (
                <div className="space-y-6">
                  {/* Informations générales */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Informations générales</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Référence:</span>
                        <p className="font-medium">{currentWeighing.reference}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Date/Heure:</span>
                        <p className="font-medium">
                          {currentWeighing.date_heure ? new Date(currentWeighing.date_heure).toLocaleString('fr-FR') : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Statut:</span>
                        <p className="font-medium">{currentWeighing.statut_display}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Type de pesée:</span>
                        <p className="font-medium">{currentWeighing.type_pesee_display}</p>
                      </div>
                    </div>
                  </div>

                  {/* Véhicule et produit */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Véhicule et Produit</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Véhicule:</span>
                        <p className="font-medium">{currentWeighing.camion_immatriculation}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Produit:</span>
                        <p className="font-medium">{currentWeighing.produit_nom}</p>
                      </div>
                    </div>
                  </div>

                  {/* Poids */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Poids</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Poids Brut:</span>
                        <p className="font-medium text-lg">{currentWeighing.poids_brut ? `${currentWeighing.poids_brut} kg` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Poids Tare:</span>
                        <p className="font-medium text-lg">{currentWeighing.poids_tare ? `${currentWeighing.poids_tare} kg` : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Poids Net:</span>
                        <p className="font-medium text-lg text-green-600">{currentWeighing.poids_net ? `${currentWeighing.poids_net} kg` : 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Partenaires */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Partenaires</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Chauffeur:</span>
                        <p className="font-medium">{currentWeighing.chauffeur_nom || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Fournisseur:</span>
                        <p className="font-medium">{currentWeighing.fournisseur_nom || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Client:</span>
                        <p className="font-medium">{currentWeighing.client_nom || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Détails supplémentaires */}
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Détails supplémentaires</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Provenance:</span>
                        <p className="font-medium">{currentWeighing.provenance || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Destination:</span>
                        <p className="font-medium">{currentWeighing.destination || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">N° Lot:</span>
                        <p className="font-medium">{currentWeighing.numero_lot || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Observations:</span>
                        <p className="font-medium">{currentWeighing.observations || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Analyse qualité si disponible */}
                  {qualityAnalysis && (
                    <div className="bg-orange-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">Analyse Qualité</h3>
                      
                      {/* Paramètres de qualité */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-sm text-gray-600">Décision:</span>
                          <p className="font-medium">
                            {qualityAnalysis.quality_decision === 'ACCEPTE' && 'Accepté'}
                            {qualityAnalysis.quality_decision === 'ACCEPTE_RESERVE' && 'Accepté avec réserve'}
                            {qualityAnalysis.quality_decision === 'REFUSE' && 'Refusé'}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Nombres de qualité:</span>
                          <p className="font-medium">{qualityAnalysis.quality_numbers || 'N/A'}</p>
                        </div>
                        
            
                        <div>
                          <span className="text-sm text-gray-600">Grainnage:</span>
                          <p className="font-medium">{qualityAnalysis.broken_grains}%</p>
                        </div>
                        
                      </div>

                      {/* Comptage des sacs */}
                      <div className="border-t border-orange-200 pt-4">
                        <h4 className="font-medium text-gray-700 mb-3">Comptage des sacs</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div className="text-center">
                            <div className="text-gray-600">Déclarés</div>
                            <div className="font-semibold text-lg">{formData.nbSacsDeclares || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-green-600">Sains</div>
                            <div className="font-semibold text-lg text-green-600">{formData.nbSacsSains || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-blue-600">Reconditionnés</div>
                            <div className="font-semibold text-lg text-blue-600">{formData.nbSacsRecond || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-orange-600">Humides</div>
                            <div className="font-semibold text-lg text-orange-600">{formData.nbSacsHumides || 0}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-red-600">Déchirés</div>
                            <div className="font-semibold text-lg text-red-600">{formData.nbSacsDechires || 0}</div>
                          </div>
                        </div>
                      </div>

                      {/* Observations */}
                      <div className="border-t border-orange-200 pt-4 mt-4">
                        <span className="text-sm text-gray-600">Observations:</span>
                        <p className="font-medium mt-1">{qualityAnalysis.observations || formData.observations || 'Aucune observation'}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Aucune information disponible</p>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowWeighingDetailsModal(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de récapitulatif après enregistrement du poids tare */}
      {showTareSummaryModal && tareSummaryData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Récapitulatif de la pesée</h2>
                  <p className="text-sm text-gray-600 mt-1">Vérifiez les informations avant de finaliser</p>
                </div>
                <button
                  onClick={() => setShowTareSummaryModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Informations principales */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 text-center">Pesée terminée avec succès</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Poids Brut</p>
                      <p className="text-lg font-bold text-green-600">
                        {tareSummaryData.poids_brut ? `${parseFloat(tareSummaryData.poids_brut.toString()).toLocaleString()} kg` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Poids Tare</p>
                      <p className="text-lg font-bold text-blue-600">
                        {tareSummaryData.poids_tare ? `${parseFloat(tareSummaryData.poids_tare.toString()).toLocaleString()} kg` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Poids Net</p>
                      <p className="text-2xl font-bold text-green-700">
                        {tareSummaryData.poids_net ? `${parseFloat(tareSummaryData.poids_net.toString()).toLocaleString()} kg` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Détails de la pesée */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Détails de la pesée</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Référence:</span>
                      <p className="font-medium">{tareSummaryData.reference}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Produit:</span>
                      <p className="font-medium">{tareSummaryData.produit || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Véhicule:</span>
                      <p className="font-medium">{tareSummaryData.vehicule || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Chauffeur:</span>
                      <p className="font-medium">{tareSummaryData.chauffeur || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Destination:</span>
                      <p className="font-medium">{tareSummaryData.destination || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">N° Lot:</span>
                      <p className="font-medium">{tareSummaryData.numero_lot || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Informations complémentaires */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Informations complémentaires</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Provenance:</span>
                      <p className="font-medium">{tareSummaryData.provenance || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Connaissement:</span>
                      <p className="font-medium">{tareSummaryData.connaissement || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">N° Transfert:</span>
                      <p className="font-medium">{tareSummaryData.numero_transfert || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sticker:</span>
                      <p className="font-medium">{tareSummaryData.sticker || 'N/A'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-sm text-gray-600">Observations:</span>
                      <p className="font-medium">{tareSummaryData.observations || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Analyse qualité */}
                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Analyse qualité</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Sacs déclarés:</span>
                      <p className="font-medium text-lg">{tareSummaryData.nb_sacs_declares || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs sains:</span>
                      <p className="font-medium text-lg text-green-600">{tareSummaryData.nb_sacs_sains || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs reconditionnés:</span>
                      <p className="font-medium text-lg text-blue-600">{tareSummaryData.nb_sacs_recond || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs humides:</span>
                      <p className="font-medium text-lg text-yellow-600">{tareSummaryData.nb_sacs_humides || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Sacs déchirés:</span>
                      <p className="font-medium text-lg text-red-600">{tareSummaryData.nb_sacs_dechires || 0}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Total analysé:</span>
                      <p className="font-medium text-lg text-purple-600">
                        {(tareSummaryData.nb_sacs_sains || 0) + 
                         (tareSummaryData.nb_sacs_recond || 0) + 
                         (tareSummaryData.nb_sacs_humides || 0) + 
                         (tareSummaryData.nb_sacs_dechires || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Partenaires */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Partenaires</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Fournisseur:</span>
                      <p className="font-medium">{tareSummaryData.fournisseur || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Client:</span>
                      <p className="font-medium">{tareSummaryData.client || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-center space-x-4 mt-8">
                <PermissionButton
                  permission={PERMISSIONS.WEIGHING_EDIT}
                  onClick={handleTareSummaryReprendre}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center"
                  title="Annuler l'enregistrement et reprendre l'étape 3"
                  disabledMessage="Permission insuffisante pour modifier une pesée"
                >
                  <i className="fas fa-undo mr-2"></i>
                  Reprendre
                </PermissionButton>
                <PermissionButton
                  permission={PERMISSIONS.WEIGHING_COMPLETE}
                  onClick={handleTareSummaryTerminer}
                  className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center"
                  title="Finaliser la pesée et passer à l'impression du reçu"
                  disabledMessage="Permission insuffisante pour compléter une pesée"
                >
                  <i className="fas fa-check-circle mr-2"></i>
                  Terminer la pesée
                </PermissionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal des pesées en attente */}
      {showPendingWeighingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-[10000]">
          <div className="bg-white h-full w-full max-w-lg shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-end items-center mb-6">
                <button
                  onClick={() => setShowPendingWeighingsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <PermissionGuard 
                permission={PERMISSIONS.WEIGHING_VIEW}
                fallback={
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">Permission insuffisante pour voir les pesées en attente</p>
                  </div>
                }
              >
                <PendingWeighingsList 
                  onWeighingSelect={(weighing) => {
                    handleSelectPendingWeighing(weighing);
                    setShowPendingWeighingsModal(false);
                  }}
                  loadedWeighingId={loadedWeighingId ?? undefined}
                />
              </PermissionGuard>
            </div>
          </div>
        </div>
      )}

      </div>
    </PermissionGuard>
  );
};

export default NouvellePeseePage;
